import { useQuery } from '@apollo/client';
import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useState } from 'react';
import AtlastkitTabs from '@atlaskit/tabs';
import { TabData } from '@atlaskit/tabs/dist/types/types';
import { FetchTaskListQuery, Task, TaskStatus, TaskType, useApprovalDeskPermissions } from '@css-xp/apd-common';
import { ErrorComponents } from '@css-xp/base';
import { useApplicationContext } from '@css-xp/base/common-context';
import { REQUEST_TYPE } from '../../constants/filters';
import { CLOSED_TASKS_TABLE_ROWS } from '../../constants/table';
import { CLOSED_TASK_STATUS, COMPLETED_TASKS_TAB, OPEN_TASKS_TAB, OPEN_TASK_STATUS } from '../../constants/task';
import { fetchTaskList } from '../../queries/tasks';
import { AdminBulkTaskActions } from '../../task-actions/admin-bulk-task-actions';
import { TaskListTable } from '../task-list-table/task-list-table';
import {
  createTableHead as createClosedTasksTableHead,
  createTableRows as createClosedTasksTableRows,
} from './admin-closed-task-list-utils';
import {
  createTableHead as createOpenTasksTableHead,
  createTableRows as createOpenTasksTableRows,
} from './admin-open-task-list-utils';
import './admin-task-list.scss';

const selectedRowsTaskIdList = new Set<string>();

const AdminTaskList = forwardRef((_props, ref) => {
  const user = useApplicationContext();
  const [taskStatus, setTaskStatus] = useState<TaskStatus>(TaskStatus.Open);
  const [selectedRows, setSelectedRows] = useState<Task[]>([]);

  const { hasTaskManagerWriteAccess } = useApprovalDeskPermissions();

  const { data, loading, error, refetch } = useQuery<FetchTaskListQuery>(fetchTaskList, {
    skip: !user?.userId,
    variables: {
      staffId: user?.userId?.split('@')?.[0],
      taskType: TaskType.AllTasks,
      taskStatus,
    },
  });

  const tabs = useMemo(() => {
    return [
      {
        label: OPEN_TASKS_TAB,
        content: '',
        testId: 'opened-requests-tab',
      },
      {
        label: COMPLETED_TASKS_TAB,
        content: '',
        testId: 'history-tab',
      },
    ];
  }, []);

  const handleTabSelect = useCallback((selected: TabData) => {
    switch (selected.label) {
      case OPEN_TASKS_TAB: {
        setTaskStatus(TaskStatus.Open);
        break;
      }
      case COMPLETED_TASKS_TAB: {
        setTaskStatus(TaskStatus.Completed);
        break;
      }
    }
  }, []);

  const resetSelectedRows = useCallback(() => {
    selectedRowsTaskIdList.clear();
    setSelectedRows([]);
  }, []);

  const selectRows = useCallback(
    (task: Task) => {
      const rows = [...selectedRows];
      for (let index = 0; index < selectedRows.length; index++) {
        const item = selectedRows[index];
        if (item.taskId === task.taskId) {
          rows.splice(index, 1);
          selectedRowsTaskIdList.delete(task.taskId);
          setSelectedRows(rows);
          return;
        }
      }
      selectedRowsTaskIdList.add(task.taskId);
      setSelectedRows(rows.concat(task));
    },
    [data, selectedRows]
  );

  const visibleRows = useMemo(() => {
    const taskslist = data?.taskList?.tasks ?? [];
    switch (taskStatus) {
      case TaskStatus.Open:
        return createOpenTasksTableRows({
          taskslist,
          refetch,
          selectRows,
          selectedRowsTaskIdList,
          hasTaskManagerWriteAccess,
        });
      case TaskStatus.Completed:
        return createClosedTasksTableRows({ taskslist });
    }
  }, [data, taskStatus, selectedRows]);

  const head = useMemo(() => {
    switch (taskStatus) {
      case TaskStatus.Open:
        return createOpenTasksTableHead(true, resetSelectedRows, selectedRowsTaskIdList, hasTaskManagerWriteAccess);
      case TaskStatus.Completed:
        return createClosedTasksTableHead(true);
    }
  }, [taskStatus, selectedRows]);

  const statusFilterOptions = useMemo(() => {
    return taskStatus === TaskStatus.Open ? OPEN_TASK_STATUS : CLOSED_TASK_STATUS;
  }, [taskStatus]);

  useImperativeHandle(ref, () => ({
    reload: refetch,
  }));

  useEffect(() => {
    if (data?.taskList) {
      resetSelectedRows();
    }
  }, [data]);

  if (error) return <ErrorComponents.GenericErrorPage error={error} />;

  return (
    <div
      className={
        taskStatus === TaskStatus.Open
          ? `admin-open-task-list${!hasTaskManagerWriteAccess ? ' no-wrt-permsn' : ''}`
          : ''
      }
    >
      <TaskListTable
        rows={visibleRows}
        head={head}
        rowsPerPage={CLOSED_TASKS_TABLE_ROWS}
        headingText=""
        isLoading={loading}
        requestTypeFilterOptions={REQUEST_TYPE}
        statusFilterOptions={statusFilterOptions}
        showDateFilters={taskStatus === TaskStatus.Open}
        showUrgentFilter={taskStatus === TaskStatus.Open}
        showResetFilter={taskStatus === TaskStatus.Open}
        bulkActions={
          hasTaskManagerWriteAccess &&
          taskStatus === TaskStatus.Open &&
          !!data?.taskList?.tasks?.length && (
            <AdminBulkTaskActions selectedRows={selectedRows} resetSelectedRows={resetSelectedRows} refetch={refetch} />
          )
        }
      >
        <div className="tabs">
          <AtlastkitTabs tabs={tabs} onSelect={handleTabSelect} />
        </div>
      </TaskListTable>
    </div>
  );
});

export default AdminTaskList;
